import {
  WSButton,
  WSButtons,
  WSContainer,
  WSElement,
  WSFlexBox,
  WSGridCentered,
  WSIconList,
  WSPanel,
  WSText,
  WSTodoList
} from "@wingspanhq/fe-component-library";
import React from "react";
import { CSVLink } from "react-csv";
import { useHistory } from "react-router-dom";
import { useBrowserPageTitle } from "../../../../components/BrowserPageTitle/BrowserPageTitle";
import { ProjectOnboardingLayout } from "../../../../components/ProjectOnboardingLayout/ProjectOnboardingLayout";
import { track } from "../../../../utils/analytics";
import styles from "./bulkUploadPayables.module.scss";

export interface BulkUploadPayablesInfoProps {}

export enum PayableStatus {
  Draft = "Draft",
  Open = "Open",
  Approved = "Approved",
  Paid = "Paid"
}

export interface DownloadPayable {
  email?: string;
  collaboratorId?: string;
  contractorId?: string;
  amount?: string;
  lineItemTitle: string;
  lineItemDescription?: string;
  lineItemHourlyRate?: string;
  lineItemHours?: string;
  reimbursable?: boolean;
  dueDate: Date;
  paidDate?: Date;
  invoiceNotes?: string;
}
export interface UploadPayable extends DownloadPayable {
  $custom?: { [key: string]: string };
}

export const BulkUploadPayablesInfo: React.FC<BulkUploadPayablesInfoProps> = () => {
  useBrowserPageTitle("Wingspan - Bulk upload payables");
  const history = useHistory();

  const downloadExamplePayables = [
    {
      email: "wade@wignspan.app",
      collaboratorId: "",
      contractorId: "",
      amount: "$100.50",
      lineItemTitle: "Pay Period 03/01-03/15",
      lineItemDescription: "Website updates & copy",
      dueDate: "2022-12-02",
      invoiceNotes: "Website Re-brand Project",
      paidDate: ""
    }
  ];
  const csvProps = {
    filename: `Wingspan_Bulk_Upload_Payables_Template.csv`,
    headers: [
      { label: "Email", key: "email" },
      { label: "Collaborator ID", key: "collaboratorId" },
      { label: "External Contractor ID", key: "contractorId" },
      { label: "Amount (USD)", key: "amount" },
      { label: "Line Item Title", key: "lineItemTitle" },
      { label: "Line Item Description", key: "lineItemDescription" },
      { label: "Due Date", key: "dueDate" },
      { label: "Invoice Notes", key: "invoiceNotes" },
      { label: "Paid Date", key: "paidDate" }
    ],
    data: downloadExamplePayables
  };

  return (
    <ProjectOnboardingLayout progress={(100 / 3) * 1}>
      <WSContainer verticalPadding>
        <WSGridCentered M={8} S={6} XS={6}>
          <WSText.Label color="grey600" mt="S" mb="M" pt="S">
            1 of 3
          </WSText.Label>
          <WSText.Heading2 mb="S">
            Bulk upload payables with a spreadsheet
          </WSText.Heading2>
          <WSPanel>
            <WSTodoList
              mb="M"
              items={[
                {
                  mark: "1.",
                  content: (
                    <>
                      <WSText.LabelBold>
                        Upload a spreadsheet of line items
                      </WSText.LabelBold>
                      <WSText.Label mt="XS">
                        Don't worry if your spreadsheet has other details, but
                        please make sure it contains the following columns.
                      </WSText.Label>
                      <WSFlexBox mt="L" mb="M">
                        <WSElement style={{ flex: 1 }}>
                          <WSText.Heading4 mb="S">Required</WSText.Heading4>
                          <WSIconList.Checks
                            items={[
                              "Email or External ID",
                              "Amount (USD)",
                              "Line Item Title",
                              "Due Date"
                            ]}
                            mb="M"
                          />
                        </WSElement>
                        <WSElement style={{ flex: 1 }}>
                          <WSText.Heading4 mb="S">Optional</WSText.Heading4>
                          <WSIconList.Checks
                            items={["Line Item Description", "Invoice Notes"]}
                            mb="M"
                          />
                        </WSElement>
                      </WSFlexBox>
                    </>
                  )
                },
                {
                  mark: "2.",
                  content: (
                    <>
                      <WSText.LabelBold>
                        Review and import line items
                      </WSText.LabelBold>
                      <WSText.Label mt="XS">
                        Review your imported line items to ensure their
                        information is correct.
                      </WSText.Label>
                    </>
                  )
                }
              ]}
            />
            <WSButtons>
              <CSVLink
                {...csvProps}
                className={styles.csvLink}
                onClick={() =>
                  history.push("/member/invoices/payables/bulk-upload/status")
                }
              >
                <WSButton.Secondary icon="download">
                  Download Example
                </WSButton.Secondary>
              </CSVLink>
              <WSButton
                className={styles.nextBtn}
                onClick={() => {
                  track("Payable Bulk Upload Started");
                  history.push("/member/invoices/payables/bulk-upload/status");
                }}
              >
                Upload Payables
              </WSButton>
            </WSButtons>
          </WSPanel>
        </WSGridCentered>
      </WSContainer>
    </ProjectOnboardingLayout>
  );
};
